import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton as MIconButton,
  Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { products as kioskProductsApi } from '../services/kioskApi';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NumericKeypad from './common/NumericKeypad';

const ProductVariationDialog = ({ 
  open, 
  onClose, 
  product, 
  onAddToCart,
  onRemoveFromCart,
  cart = []
}) => {
  const navigate = useNavigate();
  const [variations, setVariations] = useState([]);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1); // used if needed for single-variation default
  const [variationQuantities, setVariationQuantities] = useState({}); // id -> qty
  // Mini numeric keypad state
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [keypadVarId, setKeypadVarId] = useState(null);
  const [keypadEdited, setKeypadEdited] = useState(false);
  // Inline floating actions under the just-added variation
  const [lastAddedVarId, setLastAddedVarId] = useState(null);
  const [showInlineFA, setShowInlineFA] = useState(false);

  // Multi-add removed; no selection map

  // Format helper: replace hyphens/underscores, collapse spaces, and title-case words
  const prettify = (val) => {
    if (val == null) return 'Standard';
    const s = String(val)
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return s.replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Open keypad for a specific variation
  const openKeypadFor = (variationId) => {
    setKeypadVarId(variationId);
    setKeypadOpen(true);
    setKeypadEdited(false);
  };

  // Keypad handlers
  const handleKeypadInput = (digit) => {
    setVariationQuantities(prev => {
      const curVal = prev[keypadVarId] ?? 1;
      const cur = String(curVal);
      // If this is the first edit after opening, replace the default (e.g., 1) with the digit
      const base = (!keypadEdited || cur === '1') ? '' : cur;
      const next = (base + digit).slice(0, 4);
      const val = Math.max(1, parseInt(next) || 1);
      return { ...prev, [keypadVarId]: val };
    });
    setKeypadEdited(true);
  };
  const handleKeypadBackspace = () => {
    setVariationQuantities(prev => {
      const cur = String(prev[keypadVarId] ?? 1);
      const nextStr = cur.length > 1 ? cur.slice(0, -1) : '1';
      const val = Math.max(1, parseInt(nextStr) || 1);
      return { ...prev, [keypadVarId]: val };
    });
  };
  const handleKeypadClear = () => {
    setVariationQuantities(prev => ({ ...prev, [keypadVarId]: 1 }));
    setKeypadEdited(false);
  };
  const handleKeypadDone = () => {
    setKeypadOpen(false);
    setKeypadVarId(null);
    setKeypadEdited(false);
  };

  // Safely derive a human-friendly variation label from various data shapes
  const getVariationLabel = (variation) => {
    if (!variation) return 'Standard';
    // Case 1: Woo style array of attributes with .option
    if (Array.isArray(variation.attributes) && variation.attributes.length > 0) {
      const first = variation.attributes[0];
      if (first && (first.option || first.name)) {
        return prettify(first.option || first.name);
      }
    }
    // Case 2: attributes is an object map { size: { option: 'Large' } } or { size: 'Large' }
    if (variation.attributes && typeof variation.attributes === 'object') {
      const values = Object.values(variation.attributes).filter(Boolean);
      if (values.length > 0) {
        const v = values[0];
        if (v && typeof v === 'object' && (v.option || v.name)) {
          return prettify(v.option || v.name);
        }
        return prettify(v);
      }
    }
    // Case 3: fallback to variation.name if present
    if (variation.name) return prettify(variation.name);
    return 'Standard';
  };

  // Fetch variations from DB when dialog opens (GET /kiosk-products/:id/variations)
  useEffect(() => {
    const fetchVariations = async () => {
      if (!open || !product?.id) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await kioskProductsApi.getVariations(product.id);
        const variationsList = Array.isArray(data) ? data : [];

        if (variationsList.length > 0) {
          setVariations(variationsList);
          const initialQs = {};
          variationsList.forEach(v => { if (v && v.id != null) initialQs[v.id] = 1; });
          setVariationQuantities(initialQs);
          if (variationsList.length === 1) {
            setSelectedVariation(variationsList[0]);
            setQuantity(1);
          }
        } else {
          setError('No variations available for this product.');
        }
      } catch (err) {
        console.error('Error fetching variations:', err);
        setError(`Failed to load product options. ${err?.message || 'Please try again later.'}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (open && product?.id) {
      fetchVariations();
    }
  }, [open, product]);

  const handleVariationSelect = (variation) => {
    setSelectedVariation(variation);
  };

  const handleAddToCart = () => {
    if (!selectedVariation || !product || product.id == null) return;
    
    // Create a new product object that includes both parent and variation info
    const cartItemId = `${product.id}-${selectedVariation.id}`;
    const productWithVariation = {
      ...product,
      id: product.id, // This is the parent product ID
      product_id: product.id, // Explicitly set product_id
      variation_id: selectedVariation.id, // Set variation_id
      name: `${product.name} - ${getVariationLabel(selectedVariation)}`,
      price: selectedVariation.price,
      variation: selectedVariation,
      // Include variation attributes for display
      attributes: selectedVariation.attributes,
      // Set a unique ID for the cart item that includes both product and variation
      cart_item_id: cartItemId,
      // Also include 'id' for CartContext compatibility
      id: cartItemId,
      quantity: Math.max(1, parseInt(quantity) || 1)
    };
    
    console.log('Adding to cart:', productWithVariation);
    onAddToCart(productWithVariation);
    // Keep dialog open and show inline actions below this variation
    setLastAddedVarId(selectedVariation.id);
    setShowInlineFA(true);
  };

  const handleAddSpecificVariation = (variation) => {
    if (!variation || !product || product.id == null) return;
    // Single add behavior toggles: if already in cart, remove; else add
    const inCart = Array.isArray(cart) && cart.some(ci => ci.product_id === product.id && ci.variation_id === variation.id);
    if (inCart) {
      const cartItemId = `${product.id}-${variation.id}`;
      onRemoveFromCart && onRemoveFromCart(cartItemId);
      setShowInlineFA(false);
    } else {
      const qty = Math.max(1, parseInt(variationQuantities[variation.id]) || 1);
      const item = {
        ...product,
        // Provide both a unique id and cart_item_id for the cart
        id: `${product.id}-${variation.id}`,
        product_id: product.id,
        variation_id: variation.id,
        name: `${product.name} - ${getVariationLabel(variation)}`,
        price: variation.price,
        variation,
        attributes: variation.attributes,
        cart_item_id: `${product.id}-${variation.id}`,
        quantity: qty
      };
      console.log('Adding to cart (row action):', item);
      onAddToCart(item);
      // Keep dialog open and show inline actions below this variation
      setLastAddedVarId(variation.id);
      setShowInlineFA(true);
    }
  };

  // Multi-add removed; no continue action

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          m: 1.5,
          width: '100%',
          maxWidth: '840px',
          boxShadow: '0 6px 24px rgba(0,0,0,0.15)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 1.5,
        borderBottom: '1px solid #eee'
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
          {product?.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            aria-label="close" 
            onClick={onClose}
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: '#ff4d4f',
              color: 'white',
              '&:hover': { bgcolor: '#e53935' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, bgcolor: 'background.default' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box p={3}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <Box>
            <Box p={2.5} pb={1.5}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Select an option and quantity
              </Typography>
            </Box>

            <Box px={2} pb={1.5}>
              {variations.map((variation) => {
                const pid = product?.id ?? null;
                const isInCart = pid != null && Array.isArray(cart) && cart.some(ci => ci.product_id === pid && ci.variation_id === variation.id);
                const isCheap = (parseFloat(variation.price) || 0) <= 10;
                return (
                <Box key={variation.id} sx={{
                  p: 1.5,
                  mb: 1,
                  border: isInCart ? '2px solid' : '1px solid',
                  borderColor: isInCart ? 'warning.main' : '#e5e7eb',
                  borderRadius: 1.5,
                  backgroundColor: isInCart ? '#fff7ed' : 'background.paper',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  cursor: 'pointer'
                }}
                onClick={() => handleAddSpecificVariation(variation)}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getVariationLabel(variation)}
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold" sx={{ minWidth: 88, textAlign: 'right' }}>
                    {formatPrice(variation.price)}
                  </Typography>
                  {isCheap && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MIconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setVariationQuantities(prev => ({
                          ...prev,
                          [variation.id]: Math.max(1, ((parseInt(prev[variation.id]) || 1) - 1))
                        })) }}
                        disabled={(variationQuantities[variation.id] || 1) <= 1}
                      >
                        <RemoveIcon />
                      </MIconButton>
                      <TextField
                        type="number"
                        size="small"
                        value={variationQuantities[variation.id] || 1}
                        onClick={(e) => { e.stopPropagation(); openKeypadFor(variation.id); }}
                        onFocus={(e) => { e.stopPropagation(); openKeypadFor(variation.id); }}
                        onChange={(e) => { /* input handled by keypad */ }}
                        inputProps={{ min: 1, style: { textAlign: 'center', width: 70 }, readOnly: true }}
                      />
                      <MIconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setVariationQuantities(prev => ({
                          ...prev,
                          [variation.id]: (parseInt(prev[variation.id]) || 1) + 1
                        })) }}
                      >
                        <AddIcon />
                      </MIconButton>
                    </Box>
                  )}
                  {isInCart ? (
                    <MIconButton
                      aria-label="remove from cart"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); handleAddSpecificVariation(variation); }}
                      sx={{ ml: 1.5 }}
                    >
                      <DeleteOutlineIcon />
                    </MIconButton>
                  ) : (
                    <Button 
                      variant={'contained'}
                      color={'primary'}
                      onClick={(e) => { e.stopPropagation(); handleAddSpecificVariation(variation); }} 
                      sx={{ ml: 1.5 }}
                    >
                      Add to Cart
                    </Button>
                  )}
                  {/* Inline orange action bar under the just-added variation */}
                  {showInlineFA && lastAddedVarId === variation.id && (
                    <Box sx={{
                      position: 'relative',
                      width: '100%',
                      flexBasis: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      mt: 1.5
                    }}
                    onClick={(e) => e.stopPropagation()}
                    >
                      <Box sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        bgcolor: '#fff7ed',
                        border: '1px solid #fed7aa',
                        p: 1.25,
                        borderRadius: 2,
                        boxShadow: 3
                      }}>
                        <Button
                          variant="contained"
                          color="warning"
                          size="large"
                          onClick={(e) => { e.stopPropagation(); navigate('/checkout', { state: { from: 'products' } }); }}
                          sx={{ textTransform: 'none' }}
                        >
                          Checkout
                        </Button>
                        <Button
                          variant="outlined"
                          color="warning"
                          size="large"
                          onClick={(e) => { e.stopPropagation(); setShowInlineFA(false); onClose(); }}
                          sx={{ textTransform: 'none' }}
                        >
                          Continue shopping
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              );})}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #eee', justifyContent: 'space-between' }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="error" 
          startIcon={<CloseIcon />} 
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 999
          }}
        >
          Close
        </Button>
        {/* Multi-add removed: no continue button */}
      </DialogActions>
      {/* Mini numeric keypad fixed to bottom */}
      <NumericKeypad
        open={keypadOpen}
        onInput={handleKeypadInput}
        onBackspace={handleKeypadBackspace}
        onClear={handleKeypadClear}
        onDone={handleKeypadDone}
      />
    </Dialog>
  );
};

export default ProductVariationDialog;
